import React, { useState, useCallback } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaRoute,
  FaEye,
  FaPhone,
} from "react-icons/fa";

// Map styles for a clean, professional look
const mapStyles = [
  {
    featureType: "all",
    elementType: "geometry.fill",
    stylers: [{ weight: "2.00" }],
  },
  {
    featureType: "all",
    elementType: "geometry.stroke",
    stylers: [{ color: "#9c9c9c" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "all",
    stylers: [{ saturation: -100 }, { lightness: 45 }],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#eeeeee" }],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#c8d7d4" }],
  },
];

// Map container style
const containerStyle = {
  width: "100%",
  height: "100%",
};

// Collector marker icon (red)
const collectorIcon = {
  url: `data:image/svg+xml;charset=UTF-8,%3csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='20' cy='20' r='18' fill='%23ef4444' stroke='white' stroke-width='3'/%3e%3ctext x='20' y='26' text-anchor='middle' fill='white' font-size='14' font-weight='bold'%3eC%3c/text%3e%3c/svg%3e`,
  scaledSize: { width: 40, height: 40 },
};

// Research company marker icon (blue)
const researchIcon = {
  url: `data:image/svg+xml;charset=UTF-8,%3csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='14' cy='14' r='12' fill='%233b82f6' stroke='white' stroke-width='2'/%3e%3ctext x='14' y='18' text-anchor='middle' fill='white' font-size='10' font-weight='bold'%3eR%3c/text%3e%3c/svg%3e`,
  scaledSize: { width: 28, height: 28 },
};

// Key account marker icon (gold)
const keyAccountIcon = {
  url: `data:image/svg+xml;charset=UTF-8,%3csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='16' cy='16' r='14' fill='%23f59e0b' stroke='white' stroke-width='2'/%3e%3ctext x='16' y='20' text-anchor='middle' fill='white' font-size='11' font-weight='bold'%3eK%3c/text%3e%3c/svg%3e`,
  scaledSize: { width: 32, height: 32 },
};

const CollectorResearchMap = ({
  collector,
  researchCompanies = [],
  searchRadius = 100,
  onCompanySelect,
}) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Get Google Maps API key
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  // Handle marker click
  const handleMarkerClick = useCallback(
    (item, type) => {
      setSelectedMarker({ ...item, markerType: type });
      if (onCompanySelect && type === "research") {
        onCompanySelect(item);
      }
    },
    [onCompanySelect]
  );

  // Handle map load
  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="h-96 bg-gray-100 rounded flex items-center justify-center">
        <div className="text-center p-6">
          <FaMapMarkerAlt className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            Google Maps API Key Required
          </h3>
          <p className="text-gray-500">
            Configure your Google Maps API key to display the interactive map.
          </p>
        </div>
      </div>
    );
  }

  if (!collector || !collector.coordinates) {
    return (
      <div className="h-96 bg-gray-100 rounded flex items-center justify-center">
        <div className="text-center p-6">
          <FaBuilding className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            Select a Collector
          </h3>
          <p className="text-gray-500">
            Choose a collector facility to see nearby research companies on the
            map.
          </p>
        </div>
      </div>
    );
  }

  const mapCenter = {
    lat: collector.coordinates.latitude,
    lng: collector.coordinates.longitude,
  };

  // Convert miles to meters for circle radius
  const radiusInMeters = searchRadius * 1609.34;

  return (
    <div className="h-96 rounded overflow-hidden">
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} onLoad={onMapLoad}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={8}
          options={{
            styles: mapStyles,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
          }}
        >
          {/* Search radius circle */}
          <Circle
            center={mapCenter}
            radius={radiusInMeters}
            options={{
              fillColor: "#3b82f6",
              fillOpacity: 0.1,
              strokeColor: "#3b82f6",
              strokeOpacity: 0.3,
              strokeWeight: 2,
            }}
          />

          {/* Collector marker (red, larger) */}
          <Marker
            position={mapCenter}
            icon={collectorIcon}
            onClick={() => handleMarkerClick(collector, "collector")}
            title={`Collector: ${collector.name}`}
          />

          {/* Research company markers (blue, smaller) */}
          {researchCompanies.map((company, index) => (
            <Marker
              key={`research-${company.id || index}`}
              position={{
                lat: company.coordinates.latitude,
                lng: company.coordinates.longitude,
              }}
              icon={company.keyNationalAccount ? keyAccountIcon : researchIcon}
              onClick={() => handleMarkerClick(company, "research")}
              title={`${company.companyName} (${company.distance} mi)`}
            />
          ))}

          {/* Info Window for selected marker */}
          {selectedMarker && (
            <InfoWindow
              position={{
                lat:
                  selectedMarker.coordinates?.latitude ||
                  selectedMarker.coordinates?.lat,
                lng:
                  selectedMarker.coordinates?.longitude ||
                  selectedMarker.coordinates?.lng,
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="p-3 max-w-sm">
                {selectedMarker.markerType === "collector" ? (
                  // Collector info window
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-red-700">
                      📍 {selectedMarker.name}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-semibold">Type:</span>
                        <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                          {selectedMarker.facility_type}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold">Address:</span>
                        <p className="text-gray-600 mt-1">
                          {selectedMarker.address}
                        </p>
                      </div>
                      <div>
                        <span className="font-semibold">Services:</span>
                        <p className="text-gray-600 mt-1">
                          {Array.isArray(selectedMarker.services)
                            ? selectedMarker.services.join(", ")
                            : selectedMarker.services}
                        </p>
                      </div>
                      {selectedMarker.phone && (
                        <div className="flex items-center mt-3">
                          <a
                            href={`tel:${selectedMarker.phone}`}
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FaPhone />
                            <span>{selectedMarker.phone}</span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Research company info window
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-blue-700">
                      🏢 {selectedMarker.companyName}
                    </h3>
                    <div className="space-y-2 text-sm">
                      {selectedMarker.keyNationalAccount && (
                        <div>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                            ⭐ Key Account
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="font-semibold">Distance:</span>
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {selectedMarker.distance} miles
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold">Location:</span>
                        <p className="text-gray-600 mt-1">
                          {selectedMarker.location?.city},{" "}
                          {selectedMarker.location?.state}{" "}
                          {selectedMarker.location?.zipCode}
                        </p>
                      </div>
                      {selectedMarker.industrySegment && (
                        <div>
                          <span className="font-semibold">Industry:</span>
                          <p className="text-gray-600 mt-1">
                            {selectedMarker.industrySegment}
                          </p>
                        </div>
                      )}
                      {selectedMarker.territoryOwner && (
                        <div>
                          <span className="font-semibold">
                            Territory Owner:
                          </span>
                          <p className="text-gray-600 mt-1">
                            {selectedMarker.territoryOwner}
                          </p>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            selectedMarker.location?.full ||
                              `${selectedMarker.location?.street || ""} ${
                                selectedMarker.location?.city || ""
                              } ${selectedMarker.location?.state || ""} ${
                                selectedMarker.location?.zipCode || ""
                              }`.trim()
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-xs"
                        >
                          <FaRoute />
                          <span>Directions</span>
                        </a>
                        <button
                          onClick={() => {
                            if (onCompanySelect) {
                              onCompanySelect(selectedMarker);
                            }
                          }}
                          className="flex items-center space-x-1 text-green-600 hover:text-green-800 text-xs"
                        >
                          <FaEye />
                          <span>Details</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* Map legend */}
      <div className="bg-white p-3 border-t">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-xs">
              C
            </div>
            <span>Collector Facility</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs">
              R
            </div>
            <span>Research Company</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold text-xs">
              K
            </div>
            <span>Key Account</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 rounded-full border-2 border-blue-400 bg-blue-100"></div>
            <span>{searchRadius}-mile search radius</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectorResearchMap;
